import React, { useMemo, useState } from 'react';
import { MedicationRequirementType, MedicationRoute, MedicationSchedule } from '../../types';
import { useCreateMedicationMutation, useGetStudentClassesQuery } from '../../store/api/localApi';
import { useToast } from '../Toast';

const REQUIREMENT_TYPE_LABELS: Record<MedicationRequirementType, string> = {
  [MedicationRequirementType.STANDING]: 'Ongoing (given on a schedule)',
  [MedicationRequirementType.ONE_OFF]: 'One-off (a single day)',
  [MedicationRequirementType.EMERGENCY_ONLY]: 'Emergency only (no schedule)',
};

const ROUTE_LABELS: Record<MedicationRoute, string> = {
  [MedicationRoute.ORAL]: 'By mouth',
  [MedicationRoute.INHALED]: 'Inhaled',
  [MedicationRoute.TOPICAL]: 'Applied to skin',
  [MedicationRoute.INJECTION]: 'Injection',
  [MedicationRoute.OTHER]: 'Other',
};

/** 0=Sunday … 6=Saturday, matching MedicationSchedule.daysOfWeek. */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Today in the browser's locale as YYYY-MM-DD, the shape `startDate` wants. */
function todayIsoDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

interface Props {
  studentId: string;
  /** The location the requirement applies at — the child's location from
   *  `/me/children`. The server re-validates it against the child's real
   *  enrolments, so this is a convenience, not the guard. */
  locationId: string;
  onCancel: () => void;
  onCreated: () => void;
}

/**
 * The parent's "add a medication" form.
 *
 * Consent is a DISTINCT ACT, not a side effect of filling this in. The
 * `medication-consent-checkbox` here is the only consent path in the create
 * flow, and it maps 1:1 to the server's `consentConfirmed` flag — leaving it
 * unticked deliberately produces a PENDING_CONSENT requirement the parent can
 * confirm later via `medication-consent-button`. Editing a requirement never
 * consents to it.
 *
 * DAY PICKER AFFORDANCE (not the guard). The server rejects a schedule day the
 * child does not attend with 400 DAY_NOT_ENROLLED, computed from the union of
 * the child's PER-ENROLMENT `daysOfWeek` at this location. The only day data a
 * parent can read is `GET /students/{id}/classes`, which returns the CLASS's
 * running schedule — a superset of the enrolment days (an enrolment's days must
 * be a subset of its class's running days; see manage-enrollments). So the
 * picker disables days the child could not possibly attend, and the server
 * error is still surfaced inline against the day field for the residual gap
 * (a class running Mon–Fri that the child attends only Mon/Wed/Fri).
 */
const MedicationForm: React.FC<Props> = ({ studentId, locationId, onCancel, onCreated }) => {
  const { showSuccess, showError } = useToast();
  const [createMedication, { isLoading: isSaving }] = useCreateMedicationMutation();
  const { data: classesData } = useGetStudentClassesQuery(studentId);

  const [medicationName, setMedicationName] = useState('');
  const [dosage, setDosage] = useState('');
  const [route, setRoute] = useState<MedicationRoute>(MedicationRoute.ORAL);
  const [requirementType, setRequirementType] = useState<MedicationRequirementType>(
    MedicationRequirementType.STANDING
  );
  const [timesInput, setTimesInput] = useState('08:00');
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [emergencyInstructions, setEmergencyInstructions] = useState('');
  const [prescriber, setPrescriber] = useState('');
  const [startDate, setStartDate] = useState(todayIsoDate);
  const [endDate, setEndDate] = useState('');
  const [medicationExpiryDate, setMedicationExpiryDate] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const [dayError, setDayError] = useState<string | null>(null);
  const [timesError, setTimesError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const isEmergencyOnly = requirementType === MedicationRequirementType.EMERGENCY_ONLY;

  /**
   * Days the child could attend at this location — the union of the running
   * days of every class they are enrolled in HERE. Filtered by locationId
   * because a child may hold enrolments at two locations in one org and days
   * at B must not enable days at A (the same filter the server applies).
   */
  const attendableDays = useMemo(() => {
    const classes = classesData?.classes ?? [];
    const days = new Set<number>();
    for (const cls of classes) {
      if (cls.locationId !== locationId) continue;
      for (const d of cls.daysOfWeek ?? []) days.add(Number(d));
    }
    return days;
  }, [classesData, locationId]);

  // Until the classes query resolves there is nothing to disable against;
  // disabling every day on an empty set would block a legitimate submission.
  const hasDayData = attendableDays.size > 0;
  const isDayAttendable = (day: number) => !hasDayData || attendableDays.has(day);

  const toggleDay = (day: number) => {
    setDayError(null);
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const parseTimes = (): string[] =>
    timesInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDayError(null);
    setTimesError(null);
    setFormError(null);

    let schedule: MedicationSchedule | null = null;
    if (!isEmergencyOnly) {
      const timesOfDay = parseTimes();
      if (timesOfDay.length === 0) {
        setTimesError('Add at least one time, e.g. 08:00.');
        return;
      }
      const badTime = timesOfDay.find((t) => !TIME_RE.test(t));
      if (badTime) {
        setTimesError(`"${badTime}" is not a valid 24-hour time. Use HH:MM, e.g. 08:00.`);
        return;
      }
      if (selectedDays.length === 0) {
        setDayError('Pick at least one day.');
        return;
      }
      schedule = { timesOfDay, daysOfWeek: selectedDays };
    } else if (!emergencyInstructions.trim()) {
      setFormError('Emergency instructions are required for an emergency-only medication.');
      return;
    }

    try {
      await createMedication({
        studentId,
        locationId,
        medicationName: medicationName.trim(),
        requirementType,
        dosage: dosage.trim(),
        route,
        schedule,
        emergencyInstructions: emergencyInstructions.trim() || undefined,
        prescriber: prescriber.trim() || undefined,
        startDate,
        endDate: endDate || undefined,
        medicationExpiryDate: medicationExpiryDate || undefined,
        specialInstructions: specialInstructions.trim() || undefined,
        consentConfirmed,
      }).unwrap();
      showSuccess(
        consentConfirmed
          ? 'Medication added and consent recorded.'
          : 'Medication added. Confirm consent before it can be given.'
      );
      onCreated();
    } catch (err: unknown) {
      const data =
        typeof err === 'object' && err !== null && 'data' in err
          ? (err as { data?: { error?: string; message?: string; offendingDays?: number[] } }).data
          : undefined;

      // The server is the authority on enrolled days; the picker above is only
      // an affordance. Name the offending days so the parent can act.
      if (data?.error === 'DAY_NOT_ENROLLED') {
        const named = (data.offendingDays ?? []).map((d) => DAY_LABELS[d] ?? String(d)).join(', ');
        setDayError(
          named
            ? `${named}: your child is not down as attending on ${
                (data.offendingDays ?? []).length > 1 ? 'these days' : 'this day'
              }. Ask the service to update their days first.`
            : 'Your child is not down as attending on one of the days you picked.'
        );
        return;
      }
      const message = data?.error || data?.message || 'Could not add this medication.';
      setFormError(message);
      showError(message);
    }
  };

  const inputClass =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-green-500 focus:ring-green-500';

  return (
    <form
      data-testid="medication-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-lg shadow p-4 sm:p-5 space-y-4"
    >
      <h2 className="text-base font-semibold text-gray-900">Add a medication</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="medication-name" className="block text-sm font-medium text-gray-700">
            Medication name
          </label>
          <input
            id="medication-name"
            data-testid="medication-name-input"
            className={inputClass}
            value={medicationName}
            onChange={(e) => setMedicationName(e.target.value)}
            maxLength={200}
            required
          />
        </div>

        <div>
          <label htmlFor="medication-dosage" className="block text-sm font-medium text-gray-700">
            Dose
          </label>
          <input
            id="medication-dosage"
            data-testid="medication-dosage-input"
            className={inputClass}
            value={dosage}
            onChange={(e) => setDosage(e.target.value)}
            placeholder="e.g. 5ml"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label htmlFor="medication-route" className="block text-sm font-medium text-gray-700">
            How it is given
          </label>
          <select
            id="medication-route"
            data-testid="medication-route-select"
            className={inputClass}
            value={route}
            onChange={(e) => setRoute(e.target.value as MedicationRoute)}
          >
            {Object.values(MedicationRoute).map((r) => (
              <option key={r} value={r}>
                {ROUTE_LABELS[r]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="medication-type" className="block text-sm font-medium text-gray-700">
            When it is needed
          </label>
          <select
            id="medication-type"
            data-testid="medication-type-select"
            className={inputClass}
            value={requirementType}
            onChange={(e) => setRequirementType(e.target.value as MedicationRequirementType)}
          >
            {Object.values(MedicationRequirementType).map((t) => (
              <option key={t} value={t}>
                {REQUIREMENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isEmergencyOnly ? (
        <div>
          <label htmlFor="medication-emergency" className="block text-sm font-medium text-gray-700">
            When should staff use this?
          </label>
          <textarea
            id="medication-emergency"
            data-testid="medication-emergency-instructions-input"
            className={inputClass}
            rows={3}
            value={emergencyInstructions}
            onChange={(e) => setEmergencyInstructions(e.target.value)}
            maxLength={2000}
            placeholder="e.g. Use only for signs of anaphylaxis. Call 999 immediately after use."
          />
        </div>
      ) : (
        <>
          <div>
            <label htmlFor="medication-times" className="block text-sm font-medium text-gray-700">
              Times of day
            </label>
            <input
              id="medication-times"
              data-testid="medication-times-input"
              className={inputClass}
              value={timesInput}
              onChange={(e) => {
                setTimesInput(e.target.value);
                setTimesError(null);
              }}
              placeholder="08:00, 16:00"
            />
            <p className="mt-1 text-xs text-gray-500">
              24-hour times, separated by commas. Only times your child is at the service.
            </p>
            {timesError && (
              <p className="mt-1 text-xs text-red-600" data-testid="medication-times-error">
                {timesError}
              </p>
            )}
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700">Days</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAY_LABELS.map((label, day) => {
                const attendable = isDayAttendable(day);
                const selected = selectedDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    data-testid={`medication-day-toggle-${day}`}
                    disabled={!attendable}
                    aria-pressed={selected}
                    onClick={() => toggleDay(day)}
                    title={attendable ? undefined : `${label}: your child does not attend this day`}
                    className={`px-3 py-1.5 rounded-md text-sm border ${
                      !attendable
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : selected
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {hasDayData && (
              <p className="mt-1 text-xs text-gray-500" data-testid="medication-days-note">
                Greyed-out days are days your child does not attend this service. Medication can
                only be scheduled for days they are here.
              </p>
            )}
            {dayError && (
              <p className="mt-1 text-xs text-red-600" data-testid="medication-day-error">
                {dayError}
              </p>
            )}
          </fieldset>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="medication-start" className="block text-sm font-medium text-gray-700">
            Start date
          </label>
          <input
            id="medication-start"
            type="date"
            data-testid="medication-start-date-input"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="medication-end" className="block text-sm font-medium text-gray-700">
            End date <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="medication-end"
            type="date"
            data-testid="medication-end-date-input"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="medication-expiry" className="block text-sm font-medium text-gray-700">
            Medication expiry <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="medication-expiry"
            type="date"
            data-testid="medication-expiry-date-input"
            className={inputClass}
            value={medicationExpiryDate}
            onChange={(e) => setMedicationExpiryDate(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="medication-prescriber"
            className="block text-sm font-medium text-gray-700"
          >
            Prescribed by <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="medication-prescriber"
            data-testid="medication-prescriber-input"
            className={inputClass}
            value={prescriber}
            onChange={(e) => setPrescriber(e.target.value)}
            maxLength={200}
          />
        </div>
        <div>
          <label htmlFor="medication-special" className="block text-sm font-medium text-gray-700">
            Anything staff should know <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="medication-special"
            data-testid="medication-special-instructions-input"
            className={inputClass}
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            maxLength={2000}
          />
        </div>
      </div>

      {/* Consent is its own act. Unticked is a legitimate outcome — the
          requirement lands PENDING_CONSENT and can be confirmed later. */}
      <label className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 p-3">
        <input
          type="checkbox"
          data-testid="medication-consent-checkbox"
          checked={consentConfirmed}
          onChange={(e) => setConsentConfirmed(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <span className="text-sm text-gray-800">
          I consent to staff giving this medication to my child as described above. Without this,
          staff cannot give it until consent is confirmed.
        </span>
      </label>

      {formError && (
        <p className="text-sm text-red-600" data-testid="medication-form-error">
          {formError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid="medication-cancel-button"
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="medication-submit-button"
          disabled={isSaving}
          className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Add medication'}
        </button>
      </div>
    </form>
  );
};

export default MedicationForm;
